<template comments>
  <footer class="tz-footer" ref="footer">
    <div
      v-for="item in renderIf(showPageSection('alphaSelect'))"
      :key="item"
      class="tz-footer__alpha"
    >
      <div class="tz-footer__container">
        <h2
          v-for="item in renderIf(hasAlphaTitle)"
          :key="item"
          class="tz-footer__header tz-footer__header--alpha"
          v-text="footer.alphabet_title"
        />
        <ul class="tz-footer__list tz-footer__list--alpha">
          <li
            class="tz-footer__item--alpha"
            v-for="letter in alphabet"
            :key="letter"
          >
            <a
              class="tz-footer__link tz-footer__link--alpha"
              :href="getAlphaLink(isAlphaLower ? letter.toLowerCase() : letter)"
              v-text="letter"
              v-touch
            />
          </li>
        </ul>
      </div>
    </div>
    <div
      v-for="item in renderIf(hasMainContent)"
      :key="item"
      class="tz-footer__main"
    >
      <div
        v-for="item in renderIf(hasColumns)"
        :key="item"
        class="tz-footer__container"
      >
        <div class="tz-footer__columns">
          <div
            v-for="item in renderIf(showPageSection('contactInfo'))"
            :key="item"
            class="tz-footer__column tz-footer__column--primary"
          >
            <h3
              v-for="item in renderIf(footer.primary_title)"
              :key="item"
              class="tz-footer__header tz-footer__header-column"
              v-text="footer.primary_title"
            />
            <ul class="tz-footer__list">
              <li
                class="tz-footer__item tz-footer__item--link"
                v-for="link in footer.primary_list"
                :key="link.label"
              >
                <a
                  :aria-label="link.label"
                  class="tz-footer__link tz-footer__link--column"
                  :href="link.url"
                  v-touch
                >
                  <img
                    class="tz-footer__link-image"
                    :src="link.icon"
                    :alt="link.label"
                    aria-hidden="true"
                    loading="lazy"
                    :width="link.width"
                    :height="link.height"
                  />
                  <span class="tz-footer__link-text" v-text="link.label" />
                </a>
              </li>
              <li class="tz-footer__item tz-footer__item--social">
                <a
                  class="tz-footer__link tz-footer__link--social"
                  v-for="social in footer.social_media"
                  :aria-label="'Goto ' + social.label"
                  :key="social.label"
                  :href="social.url"
                  target="blank"
                  rel="noopener noreferrer"
                  v-touch
                >
                  <img
                    :src="social.icon"
                    alt="social.label"
                    aria-hidden="true"
                    class="tz-footer__link-icon"
                    loading="lazy"
                    :width="social.width"
                    :height="social.height"
                  />
                </a>
              </li>
              <li class="tz-footer__item tz-footer__item--ratings">
                <label class="tz-footer__label" v-text="footer.ratings_title" />
                <img
                  :src="footer.ratings_image"
                  alt="Five Star Rating"
                  aria-label="Five Star Rating"
                  loading="lazy"
                  :width="footer.ratings_image_width"
                  :height="footer.ratings_image_height"
                />
              </li>
              <li class="tz-footer__item tz-footer__item--secure">
                <slot name="badges"></slot>
              </li>
            </ul>
          </div>
          <div
            v-for="item in renderIf(showPageSection('columnSecondary'))"
            :key="item"
            class="tz-footer__column tz-footer__column--secondary"
          >
            <h3
              v-for="item in renderIf(footer.secondary_title)"
              :key="item"
              class="tz-footer__header tz-footer__header-column"
              v-text="footer.secondary_title"
            />
            <ul class="tz-footer__list">
              <li
                class="tz-footer__item tz-footer__item--link"
                v-for="link in footer.secondary_list"
                :key="link.label"
              >
                <a
                  class="tz-footer__link tz-footer__link--column"
                  :href="link.url"
                  v-text="link.label"
                  v-touch
                />
              </li>
            </ul>
          </div>
          <div
            v-for="item in renderIf(showPageSection('columnTertiary'))"
            :key="item"
            class="tz-footer__column tz-footer__column--tertiary"
          >
            <h3
              v-for="item in renderIf(footer.tertiary_title)"
              :key="item"
              class="tz-footer__header tz-footer__header-column"
              v-text="footer.tertiary_title"
            />
            <ul class="tz-footer__list">
              <li
                class="tz-footer__item tz-footer__item--link"
                v-for="link in footer.tertiary_list"
                :key="link.label"
              >
                <a
                  class="tz-footer__link tz-footer__link--column"
                  :href="link.url"
                  v-text="link.label"
                  v-touch
                />
              </li>
            </ul>
          </div>
          <div
            v-for="item in renderIf(showPageSection('columnQuarternary'))"
            :key="item"
            class="tz-footer__column tz-footer__column--quarternary"
          >
            <h3
              v-for="item in renderIf(footer.quarternary_title)"
              :key="item"
              class="tz-footer__header tz-footer__header-column"
              v-text="footer.quarternary_title"
            />
            <ul class="tz-footer__list">
              <li
                class="tz-footer__item tz-footer__item--link"
                v-for="link in footer.quarternary_list"
                :key="link.label"
              >
                <a
                  class="tz-footer__link tz-footer__link--column"
                  :href="link.url"
                  v-text="link.label"
                  v-touch
                />
              </li>
            </ul>
          </div>
        </div>
        <button
          class="tz-footer__scroll-to-top"
          :class="{ 'tz-footer__scroll-to-top--active': showScrollTop() }"
          title="Go to top"
          id="scrollToTop"
          @click="scrollToTop"
          aria-label="Scroll to Top of Page"
        >
          <img
            v-for="item in renderIf(scrollTopHoverImage)"
            :key="item"
            class="tz-footer__scroll-image"
            :src="scrollTopHoverImage"
            alt="Scroll To Top"
            aria-hidden="true"
            loading="lazy"
          />
        </button>
      </div>
      <div
        v-for="item in renderIf(showPageSection('disclaimer'))"
        :key="item"
        class="tz-footer__container tz-footer__container--disclaimer"
        ref="disclaimer"
      >
        <!--googleoff: index-->
        <tz-markdown
          :class="'tz-footer__disclaimer-text'"
          :content="footer.disclaimer"
        />
        <!--googleon: index-->
      </div>
    </div>
    <div class="tz-footer__global">
      <div class="tz-footer__container tz-footer__container--global">
        <div class="tz-footer__copyright" v-html="footer.copyright" />
        <ul
          v-for="item in renderIf(showPageSection('globalLinks'))"
          :key="item"
          class="tz-footer__list tz-footer__list--global"
        >
          <li
            class="tz-footer__item tz-footer__item--global"
            v-for="link in footer.global_list"
            :key="link.label"
          >
            <a
              class="tz-footer__link"
              :href="link.url"
              :aria-label="'Goto ' + link.label"
              v-text="link.label"
              v-touch
            />
          </li>
        </ul>
      </div>
    </div>
  </footer>
</template>

<script>
import Vue from 'vue';
import Vue2TouchEvents from 'vue2-touch-events';
import TzMarkdown from '@trazi/tz-markdown/src/tz-markdown.vue';
import tracking from './assets/js/tracking.js';

Vue.use(Vue2TouchEvents);

export default {
  name: 'TzFooter',
  components: {
    TzMarkdown
  },
  comments: true,
  computed: {
    hasAlphaTitle() {
      return typeof this.footer.alphabet_title === 'string';
    },
    hasColumns() {
      return (
        this.showPageSection('contactInfo') ||
        this.showPageSection('columnSecondary') ||
        this.showPageSection('columnTertiary') ||
        this.showPageSection('columnQuarternary')
      );
    },
    hasMainContent() {
      return this.hasColumns || this.showPageSection('disclaimer');
    }
  },
  data() {
    return {
      alphabet: [
        'A',
        'B',
        'C',
        'D',
        'E',
        'F',
        'G',
        'H',
        'I',
        'J',
        'K',
        'L',
        'M',
        'N',
        'O',
        'P',
        'Q',
        'R',
        'S',
        'T',
        'U',
        'V',
        'W',
        'X',
        'Y',
        'Z'
      ],
      hasScripts: false,
      headerHeight: null
    };
  },
  methods: {
    /**
     * If statement vue hack to prevent <!-- --> comment
     * @param condition
     * @returns {[]}
     */
    renderIf(condition) {
      return condition ? [Math.random()] : [];
    },
    filterDirectMatches(routes) {
      return routes.filter(function(route) {
        return !route.endsWith('*');
      });
    },
    filterStartWithMatches(routes) {
      return routes.filter(function(route) {
        return route.endsWith('*');
      });
    },
    getAlphaLink(letter) {
      return (
        this.alphaBaseLink +
        letter +
        (this.alphaLinkPad ? '000000' : '') +
        (this.hasTrailingSlash ? '/' : '')
      );
    },
    handleAddingScripts() {
      // Ensure Scripts can only be added once
      if (this.hasScripts) {
        return;
      }
      const tracking_scripts = this.footer.tracking_scripts;
      // Go through all tracking scripts
      for (let i = 0; i < tracking_scripts.length; i++) {
        // Add tracking defaults to true if there are no page filters
        let addTracking = !tracking_scripts[i].route_filters;

        // If there are page filters
        if (tracking_scripts[i].route_filters) {
          const include = tracking_scripts[i].route_filters[0].include;
          const exclude = tracking_scripts[i].route_filters[0].exclude;

          // If all filters are empty, add to all pages
          if (!include && !exclude) {
            addTracking = true;
          }

          // If there are includes, add tracking if this page matches the filter
          addTracking = include
            ? this.isMatchToTrackingFilter(include)
            : addTracking;

          // Excludes will override any matching includes.
          // If there are excludes, do not add tracking if this page matches the filter.
          addTracking = exclude
            ? !this.isMatchToTrackingFilter(exclude)
            : addTracking;

          // MightyHive Id is based on page name
          if (addTracking && tracking_scripts[i].key === 'mightyHive') {
            tracking_scripts[i].route_data[0].remar =
              this.page.name === 'landing'
                ? '0'
                : this.page.name === 'arrest-records'
                ? '00'
                : '000';
          }

          // Provider id required to correctly filter gtm tags
          if (addTracking && tracking_scripts[i].key === 'gtm') {
            tracking_scripts[i].route_data[0].providerId = this.page.providerId;
          }
        }

        // if add tracking, run that key
        if (addTracking) {
          tracking[tracking_scripts[i].key](tracking_scripts[i].route_data[0]);
        }
      }

      this.hasScripts = true;
    },
    hasDirectMatch(routes) {
      return routes.includes(this.page.name);
    },
    isMatchToTrackingFilter(filters) {
      let routes = this.splitRoutes(filters);
      // Grab all the direct matches
      let directMatches = this.filterDirectMatches(routes);
      // Grab all the Starting Matches
      let startsWithMatches = this.filterStartWithMatches(routes);
      // Return true if page matches direct or starting with filter
      return (
        this.hasDirectMatch(directMatches) ||
        this.isStartingWith(startsWithMatches)
      );
    },
    isStartingWith(routes) {
      // Go through every route
      for (let s = 0; s < routes.length; s++) {
        // Remove the wildcard
        let routeName = routes[s].replace('*', '');
        // If this page route starts with the route name, return true
        if (this.page.name.startsWith(routeName)) {
          return true;
        }
      }

      return false;
    },
    showPageSection(section) {
      return this.displaySections.includes(section);
    },
    showScrollTop() {
      return this.showScrollToTop && this.headerFixed;
    },
    scrollToTop() {
      this.$emit('scroll-to-top');
    },
    splitRoutes(routes) {
      return routes.replace(/\s/g, '').split(',');
    }
  },
  mounted() {
    if (this.addScripts) {
      this.handleAddingScripts();
    }
  },
  watch: {
    addScripts: function() {
      this.handleAddingScripts();
    }
  },
  props: {
    // If initially true, load tracking scripts on mounted. Otherwise only load when prop is changed to true
    addScripts: {
      type: Boolean,
      default: true
    },
    // The base link for all the alphabet index pages
    alphaBaseLink: {
      type: String,
      required: true
    },
    // True if alphabet link requires padding with 0s
    alphaLinkPad: {
      type: Boolean,
      default: false
    },
    // An array of the sections to be displayed
    displaySections: {
      type: Array
    },
    // CMS data for page
    footer: {
      type: Object,
      required: true
    },
    // Indicates if header has become fixed to page - indicates if scroll to top is active when displayed
    headerFixed: {
      type: Boolean,
      required: true
    },
    // Page data as component does not have vue router. Expected format {path: string, name: string, params: {}}
    page: {
      type: Object,
      required: true
    },
    // Optional Hover Image for Scroll Top Button
    scrollTopHoverImage: {
      type: String,
      default: null
    },
    // True only if scroll to top button should display on page
    showScrollToTop: {
      type: Boolean,
      default: false
    },
    // Adds trailing slash to links
    hasTrailingSlash: {
      type: Boolean,
      default: false
    },
    // Lowercase alphabet links
    isAlphaLower: {
      type: Boolean,
      default: false
    }
  }
};
</script>
